import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';

const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [page, setPage]           = useState(1);
  const [total, setTotal]         = useState(0);
  const [pages, setPages]         = useState(1);
  const [selected, setSelected]   = useState(null); // detail modal

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/customers', { params: { search, page, limit: 15 } });
      setCustomers(res.data.customers);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch {
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  // Debounce search
  useEffect(() => { setPage(1); }, [search]);

  const openDetail = async (id) => {
    try {
      const res = await api.get(`/customers/${id}`);
      setSelected(res.data);
    } catch {}
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 800 }}>Customers</h1>
          <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>{total} total customers</p>
        </div>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '1rem' }}>
        <input
          className="form-input"
          placeholder="Search by name or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 360 }}
        />
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              {['Name', 'Email', 'Phone', 'Orders', 'Total Spent', 'Joined', ''].map(h => (
                <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 700, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading...</td></tr>
            ) : customers.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>No customers found.</td></tr>
            ) : customers.map(c => (
              <tr key={c.id} style={{ borderBottom: '1px solid #f3f4f6' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={e => e.currentTarget.style.background = ''}
              >
                <td style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%',
                      background: '#2563eb', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 800, fontSize: '0.8rem', flexShrink: 0,
                    }}>
                      {c.name?.charAt(0).toUpperCase()}
                    </div>
                    {c.name}
                  </div>
                </td>
                <td style={{ padding: '0.75rem 1rem', color: '#374151' }}>{c.email}</td>
                <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{c.phone || '—'}</td>
                <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                  <span style={{
                    background: c.total_orders > 0 ? '#dbeafe' : '#f3f4f6',
                    color: c.total_orders > 0 ? '#1e40af' : '#6b7280',
                    padding: '0.2rem 0.6rem', borderRadius: 20,
                    fontWeight: 700, fontSize: '0.75rem',
                  }}>
                    {c.total_orders}
                  </span>
                </td>
                <td style={{ padding: '0.75rem 1rem', fontWeight: 700, color: '#15803d' }}>
                  ${parseFloat(c.total_spent).toFixed(2)}
                </td>
                <td style={{ padding: '0.75rem 1rem', color: '#6b7280', fontSize: '0.8rem' }}>
                  {new Date(c.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                </td>
                <td style={{ padding: '0.75rem 1rem' }}>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => openDetail(c.id)}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '1rem' }}>
          <button className="btn btn-outline btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span style={{ padding: '0.4rem 0.75rem', fontSize: '0.85rem', color: '#6b7280' }}>
            Page {page} of {pages}
          </span>
          <button className="btn btn-outline btn-sm" disabled={page === pages} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}

      {/* Detail Modal */}
      {selected && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={() => setSelected(null)}
        >
          <div
            style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 560, maxHeight: '85vh', overflow: 'auto', padding: '1.5rem' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ fontWeight: 800, fontSize: '1.1rem' }}>Customer Details</h2>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#6b7280' }}>✕</button>
            </div>

            {/* Info */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' }}>
              <div style={{
                width: 52, height: 52, borderRadius: '50%',
                background: '#2563eb', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: '1.2rem',
              }}>
                {selected.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1rem' }}>{selected.name}</div>
                <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>{selected.email}</div>
                {selected.phone && <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>{selected.phone}</div>}
              </div>
            </div>

            {/* Order History */}
            <h3 style={{ fontWeight: 700, marginBottom: '0.75rem', fontSize: '0.95rem' }}>
              Order History ({selected.orders?.length || 0})
            </h3>
            {selected.orders?.length === 0 ? (
              <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>No orders yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {selected.orders?.map(o => (
                  <div key={o.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '0.6rem 0.75rem', background: '#f9fafb', borderRadius: 8,
                    fontSize: '0.85rem',
                  }}>
                    <div>
                      <span style={{ fontWeight: 700 }}>#{o.order_number}</span>
                      <span style={{ color: '#6b7280', marginLeft: '0.5rem' }}>
                        {new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <span style={{
                        background: o.payment_status === 'paid' ? '#dcfce7' : '#fef9c3',
                        color: o.payment_status === 'paid' ? '#15803d' : '#854d0e',
                        padding: '0.15rem 0.5rem', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700,
                      }}>{o.payment_status}</span>
                      <span style={{ fontWeight: 800 }}>${parseFloat(o.total).toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCustomers;
